import * as React from 'react';

import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ServiceStatus } from '@rematter/frontend-lib-api-client';
import { cn } from '@rematter/ui-web';
import { cva } from 'class-variance-authority';

import { getStatusBorderClass } from '../../utils/serviceColors';

const kanbanTaskVariants = cva(
  'flex w-[250px] rounded-lg border border-foreground/10 bg-space p-0.5 select-none',
  {
    variants: {
      selected: {
        true: 'ring-2 ring-primary',
      },
      dragging: {
        over: 'cursor-grabbing opacity-30 ring-2',
        overlay: 'cursor-grabbing ring-2 ring-primary',
      },
    },
  },
);

export interface KanbanTask {
  id: UniqueIdentifier;
  columnId: UniqueIdentifier;
  rank: string;
  routeId?: string;
  routeName?: string;
  routeStatus?: string;
  routePosition?: number;
  status?: string;
  selected?: boolean;
  pending?: boolean;
  content: () => React.ReactNode;
}

interface TaskCardProps {
  task: KanbanTask;
  isOverlay?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  className?: string;
}

export interface TaskDragData {
  type: 'Task';
  task: KanbanTask;
}

export interface RouteGroupDragData {
  type: 'RouteGroup';
  routeId: string;
  routeName: string;
  tasks: KanbanTask[];
}

export const TaskCard = ({
  task,
  onClick,
  isOverlay,
  disabled,
  className,
}: TaskCardProps) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    disabled,
    data: {
      type: 'Task',
      task,
    } satisfies TaskDragData,
    attributes: {
      roleDescription: 'Task',
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        '@TaskCard',
        kanbanTaskVariants({
          selected: task.selected,
          dragging: isOverlay ? 'overlay' : isDragging ? 'over' : undefined,
          className,
        }),
        task.pending && 'border-dashed border-foreground/20 bg-white',
        !task.pending && task.status && 'border-l-4',
        !task.pending &&
          task.status &&
          getStatusBorderClass(task.status as ServiceStatus),
      )}
      data-task-card
      {...attributes}
      {...listeners}
      onClick={!isOverlay ? onClick : undefined}
    >
      {task.content()}
    </div>
  );
};
